<!--
  PICKUP & DELIVERY — scheda di creazione.

  - Due passi con nome, in cima alla card.
  - ANDATA e RITORNO sono sezioni che si accendono con l'interruttore nel
    titolo; una sezione spenta resta visibile, in grigio.
  - Il totale sta accanto al pulsante che conferma.
  - Il PACCHETTO VALET sta in «Cliente e veicolo»: è un attributo dell'AUTO
    (`minute` è legata a `car`), non della tratta.
-->
<template>
  <div class="servizio-wrap fx">
    <!-- ---------- le due schermate della scheda ----------

         ⚠️ NIENTE NUMERI QUI. La pagina che ospita questa scheda numera già i
         suoi passi — «1 Servizio», «2 Pickup & Delivery» — e mettere subito
         sotto un altro contatore che riparte da 1 faceva comparire il cerchio
         ② seguito dal cerchio ①: due sequenze attaccate, entrambe al primo
         posto, che si leggevano come un errore di impaginazione.

         Queste due non sono un secondo livello di numerazione: sono le due
         schermate DENTRO un passo che la pagina ha già numerato. Quindi si
         dice dove si e', non a che numero si e'. -->
    <nav
      class="fx__flow"
      aria-label="Schermate della scheda"
    >
      <span
        class="fx__flowStep"
        :class="{ 'is-on': active === 0 }"
        :aria-current="active === 0 ? 'step' : undefined"
      >Prenotazione</span>
      <span
        class="fx__flowSep"
        aria-hidden="true"
      >›</span>
      <span
        class="fx__flowStep"
        :class="{ 'is-on': active === 1 }"
        :aria-current="active === 1 ? 'step' : undefined"
      >Lavori e allegati</span>
    </nav>

    <!-- ================= PASSO 1 ================= -->
    <template v-if="active === 0">
      <!-- ---------- cliente e veicolo ---------- -->
      <section class="form-card">
        <header class="form-card__head">
          <h3>Cliente e veicolo</h3>
          <div class="form-card__spacer" />
          <AddNewCustomerComponent servizio="pickup" />
        </header>

        <div class="form-card__body">
          <div class="container-customer">
            <div>
              <PointComponent
                :value="customerExist"
                title="Cerca cliente per email"
                label="email"
                placeholder="email"
                @input="(newValue) => (customerExist = newValue)"
              />
            </div>
            <div>
              <ButtonComponent
                label="cerca"
                :is-router="false"
                @button-click="checkCustomerExist"
              />
            </div>
          </div>

          <div class="flex-container">
            <div class="select-component">
              <SelectComponent
                :options="customersId"
                title="Cliente"
                :add-buttom="false"
                :highlight="highlightCustomer"
                :is-disabled="false"
                :span="6"
                :value="customerSelected"
                @selected="handleChangeCustomer"
              />
            </div>

            <div class="select-wrapper select-component">
              <SelectComponent
                :options="carsId"
                title="Auto"
                :add-buttom="true"
                :highlight="highlightCar"
                :link-path="`/add-car/${customerSelected}`"
                :is-disabled="!customerSelected"
                :value="carSelected"
                :span="6"
                @selected="handleChangeCar"
              />
            </div>

            <div class="select-component">
              <SelectComponent
                :options="headquartersId"
                title="Sede"
                :add-buttom="false"
                :highlight="highlightHeadquarter"
                :is-disabled="false"
                :span="6"
                :value="headquarterSelected"
                @selected="handleChangeHeadquarter"
              />
            </div>
          </div>

          <!-- L'auto di cortesia non esiste nel notturno: il cliente lo si
               accompagna, non gli si sostituisce l'auto. -->
          <div
            v-if="!isNotturno"
            class="form-sub"
          >
            <div class="fx-field">
              <span class="campo-label">Auto di cortesia</span>
              <el-radio-group
                v-model="booking.loaner"
                class="fx-radio"
                @change="changeLoaner"
              >
                <el-radio label="true">
                  Sì
                </el-radio>
                <el-radio label="false">
                  No
                </el-radio>
              </el-radio-group>
            </div>

            <div
              v-if="booking.loaner == 'true'"
              class="fx-field fx-field--targa"
            >
              <span class="campo-label">Targa auto di cortesia</span>
              <el-input
                v-model="booking.targa_loaner"
                placeholder="Es. GH 881 TZ"
              />
            </div>
          </div>

          <!-- ---- pacchetto valet dell'auto ----
               Legato all'AUTO, non al cliente: sta qui, accanto all'auto.

               ⚠️ Il valet lo applica il SERVER alla creazione, se l'auto ha un
               pacchetto usabile (`consumeValetOnCreate`). L'operatore può non
               usarlo: con l'interruttore spento il form manda `use_valet: false`
               e la corsa si paga a tariffa. Acceso non si manda nulla (è il
               default del backend). Qui si mostra quale pacchetto userà e quanto
               verrà a costare la corsa, con la stessa regola: la corsa è
               compresa, si pagano solo i km di ogni tratta oltre quelli del
               pacchetto, al €/km della fascia valet. -->
          <div class="vl">
            <span class="campo-label">Pacchetto valet</span>

            <p
              v-if="!booking.car || !booking.car.id"
              class="vl__empty"
            >
              Scegli l'auto per vedere se ha un pacchetto valet.
            </p>
            <p
              v-else-if="!valetInUso"
              class="vl__empty"
            >
              Nessun pacchetto valet utilizzabile per quest'auto: la corsa si paga a tariffa.
            </p>

            <div
              v-else
              class="vl__card"
            >
              <div class="vl__top">
                <span class="vl__title">
                  {{ valetInUso.valet_per_year ? `${valetInUso.valet_per_year} valet all'anno` : 'Pacchetto valet' }}
                </span>
                <span
                  v-if="applicaValet"
                  class="vl__tag"
                >Si applica a questa prenotazione</span>
                <span class="form-card__spacer" />
                <label class="vl__switch">
                  <el-switch
                    v-model="applicaValet"
                    aria-label="Applica il valet a questa prenotazione"
                  />
                  <span>Applica il valet a questa prenotazione</span>
                </label>
              </div>

              <div class="vl__stats">
                <div class="vl__stat">
                  <span class="vl__k">Residui</span>
                  <span class="vl__v">
                    <b>{{ Number(valetInUso.residual_valet) || 0 }}</b>
                    <small>di {{ Number(valetInUso.valet) || 0 }}</small>
                  </span>
                  <span class="vl__bar">
                    <span :style="{ width: `${percentualeValet(valetInUso)}%` }" />
                  </span>
                </div>
                <div class="vl__stat">
                  <span class="vl__k">Scade il</span>
                  <span class="vl__v">{{ dataBreve(valetInUso.expiration_valet) }}</span>
                </div>
                <div class="vl__stat">
                  <span class="vl__k">Km compresi</span>
                  <span class="vl__v">{{ valetInUso.included_km == null ? 'tutti' : `fino a ${numeroIt(valetInUso.included_km)} km` }}</span>
                </div>
              </div>

              <div
                class="vl__effect"
                aria-live="polite"
              >
                <template v-if="!applicaValet">
                  <b>Valet non applicato: la corsa si paga a tariffa.</b>
                  Il pacchetto resta con {{ Number(valetInUso.residual_valet) || 0 }} valet residui.
                </template>
                <template v-else-if="stimaValet.stato === 'attesa'">
                  Il costo con il valet si calcola appena ci sono i km delle tratte.
                </template>
                <template v-else-if="stimaValet.stato === 'calcolo'">
                  Calcolo del costo con il valet…
                </template>
                <template v-else-if="stimaValet.stato === 'ok' && stimaValet.kmEccedenza === 0">
                  <b>La corsa è compresa nel valet: {{ euroTesto0(0) }}.</b>
                  <span v-if="valetInUso.included_km != null"> {{ stimaValet.tratte > 1 ? 'Andata e ritorno rientrano' : 'La tratta rientra' }} nei {{ numeroIt(valetInUso.included_km) }} km compresi.</span>
                </template>
                <template v-else-if="stimaValet.stato === 'ok'">
                  <b>Si pagano solo i {{ numeroIt(stimaValet.kmEccedenza) }} km in più: {{ euroTesto0(stimaValet.prezzo) }} + IVA.</b>
                  {{ numeroIt(valetInUso.included_km) }} km compresi {{ stimaValet.tratte > 1 ? 'per tratta' : 'nel pacchetto' }}, i km in più a {{ euroTesto0(stimaValet.prezzoKm) }}/km.
                </template>
                <template v-else-if="stimaValet.stato === 'mancante'">
                  {{ numeroIt(stimaValet.kmEccedenza) }} km oltre quelli compresi, ma il prezzo al km dei km in più non è impostato nelle Tariffe.
                </template>
                <template v-else-if="stimaValet.stato === 'errore'">
                  Non riesco a calcolare il costo con il valet: lo calcola Simba alla creazione.
                </template>
              </div>

              <p
                v-if="pacchettiUsabili.length > 1"
                class="vl__more"
              >
                L'auto ha altri {{ pacchettiUsabili.length - 1 }} {{ pacchettiUsabili.length === 2 ? 'pacchetto' : 'pacchetti' }} utilizzabili: si usa prima quello che scade prima.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- ---------- andata ---------- -->
      <section
        class="form-card"
        :class="{ 'is-off': !is_gone }"
      >
        <header class="form-card__head">
          <el-switch
            v-model="is_gone"
            :active-value="true"
            :inactive-value="false"
            aria-label="Includi l'andata"
          />
          <h3>Andata</h3>
          <div class="form-card__spacer" />
          <span class="form-card__hint">Ritiro dell'auto dal cliente</span>
        </header>

        <div
          v-if="is_gone"
          class="form-card__body"
        >
          <el-form :model="pickup_gone">
            <div class="container-data">
              <DateSelectComponent
                title="Data di arrivo in sede"
                slot-title="Fascia oraria"
                :selected-date="dataIniziale"
                :loading="loadingSlotsGone"
                :time-slots="timeSlotsGone"
                :highlight-slot="highlightSlotGone"
                :date-disabled="!customerSelected || !headquarterSelected"
                :highlight-date="highlightDateGone"
                :selected-slot-input="selectedSlotGone"
                :disabled-dates="disabledDate"
                @selected-slot="handleSlotChangeGone"
                @date-partner-change="handleChangeDateGone"
              />
            </div>

            <div class="container-point">
              <PointComponent
                :value="pickup_gone.point.address"
                title="Indirizzo di prelievo"
                label="Indirizzo"
                placeholder="Indirizzo"
                :span="8"
                @input="(newValue) => (pickup_gone.point.address = newValue)"
                @focus="handleInputFocusGone"
                @blur="handleInputBlurGone"
              />

              <PointComponent
                :value="pickup_gone.point.street_number"
                title="Civico"
                label="N. civico"
                placeholder="N. civico"
                @input="(newValue) => (pickup_gone.point.street_number = newValue)"
                @focus="handleInputFocusGone"
                @blur="handleInputBlurGone"
              />

              <PointComponent
                :value="pickup_gone.point.cap"
                title="CAP"
                label="CAP"
                placeholder="CAP"
                @input="(newValue) => (pickup_gone.point.cap = newValue)"
                @focus="handleInputFocusGone"
                @blur="handleInputBlurGone"
              />

              <PointComponent
                :value="pickup_gone.point.city"
                :span="4"
                title="Città"
                label="Città"
                placeholder="Città"
                @input="(newValue) => (pickup_gone.point.city = newValue)"
                @focus="handleInputFocusGone"
                @blur="handleInputBlurGone"
              />

              <PointComponent
                :value="pickup_gone.point.province"
                title="Provincia"
                label="Provincia"
                placeholder="Provincia"
                @input="(newValue) => (pickup_gone.point.province = newValue)"
                @focus="handleInputFocusGone"
                @blur="handleInputBlurGone"
              />
            </div>

            <DescriptionComponent
              :label="pickup_gone.point.label"
              :loading="loadingPoint"
            />

            <!-- Km e tariffa accanto ai campi che li producono: finché il
                 calcolo non c'è si legge «—», non «0 km» o «0 euro». -->

            <div class="fx-stats">
              <!-- La FASCIA del listino in cui rientra la tratta (è quella che
                   fa il prezzo), e sotto, più piccoli, i km e i minuti veri. -->
              <span class="fx-stat fx-stat--fascia">
                <span class="fx-stat__l">Fascia km</span>
                <span class="fx-stat__v">{{ fasciaTesto(pickup_gone.simba_km, responseKmGone, pickup_gone.date_partner) }}</span>
                <span
                  v-if="Number(pickup_gone.simba_km) > 0"
                  class="fx-stat__sub"
                >{{ percorsoTesto(pickup_gone) }}</span>
              </span>

              <span
                class="fx-stat fx-stat--euro"
                :class="{ 'fx-stat--barrata': valetApplicato }"
              >
                <span class="fx-stat__l">Tariffa</span>
                <span class="fx-stat__v">{{ euroTesto(responseCostGone) }}</span>
              </span>
            </div>
          </el-form>
        </div>

        <!-- La sezione spenta non sparisce: si vede che esiste e cosa
             conterrebbe, e la pagina non salta accendendola. -->
        <div
          v-else
          class="form-card__off"
        >
          Non inclusa in questa prenotazione. Accendila con l'interruttore.
        </div>
      </section>

      <!-- ---------- ritorno ---------- -->
      <section
        class="form-card"
        :class="{ 'is-off': !is_return }"
      >
        <header class="form-card__head">
          <el-switch
            v-model="is_return"
            :active-value="true"
            :inactive-value="false"
            aria-label="Includi il ritorno"
          />
          <h3>Ritorno</h3>
          <div class="form-card__spacer" />
          <span class="form-card__hint">Riconsegna dell'auto al cliente</span>
        </header>

        <div
          v-if="is_return"
          class="form-card__body"
        >
          <el-form :model="pickup_return">
            <div class="container-data">
              <DateSelectComponent
                title="Data di ritiro dalla sede"
                slot-title="Fascia oraria"
                :loading="loadingSlotsReturn"
                :time-slots="timeSlotsReturn"
                :highlight-slot="highlightSlotReturn"
                :date-disabled="!customerSelected || !headquarterSelected"
                :highlight-date="highlightDateReturn"
                :selected-slot-input="selectedSlotReturn"
                :disabled-dates="disabledDateReturn"
                @selected-slot="handleSlotChangeReturn"
                @date-partner-change="handleChangeDateReturn"
              />
            </div>

            <div class="container-point">
              <PointComponent
                :value="pickup_return.point.address"
                title="Indirizzo di consegna"
                label="Indirizzo"
                placeholder="Indirizzo"
                :span="8"
                @input="(newValue) => (pickup_return.point.address = newValue)"
                @focus="handleInputFocusReturn"
                @blur="handleInputBlurReturn"
              />

              <PointComponent
                :value="pickup_return.point.street_number"
                title="Civico"
                label="N. civico"
                placeholder="N. civico"
                @input="(newValue) => (pickup_return.point.street_number = newValue)"
                @focus="handleInputFocusReturn"
                @blur="handleInputBlurReturn"
              />

              <PointComponent
                :value="pickup_return.point.cap"
                title="CAP"
                label="CAP"
                placeholder="CAP"
                @input="(newValue) => (pickup_return.point.cap = newValue)"
                @focus="handleInputFocusReturn"
                @blur="handleInputBlurReturn"
              />

              <PointComponent
                :value="pickup_return.point.city"
                :span="4"
                title="Città"
                label="Città"
                placeholder="Città"
                @input="(newValue) => (pickup_return.point.city = newValue)"
                @focus="handleInputFocusReturn"
                @blur="handleInputBlurReturn"
              />

              <PointComponent
                :value="pickup_return.point.province"
                title="Provincia"
                label="Provincia"
                placeholder="Provincia"
                @input="(newValue) => (pickup_return.point.province = newValue)"
                @focus="handleInputFocusReturn"
                @blur="handleInputBlurReturn"
              />
            </div>

            <DescriptionComponent
              :label="pickup_return.point.label"
              :loading="loadingPoint"
            />

            <!-- Km e tariffa accanto ai campi che li producono: finché il
                 calcolo non c'è si legge «—», non «0 km» o «0 euro». -->

            <div class="fx-stats">
              <!-- La FASCIA del listino in cui rientra la tratta (è quella che
                   fa il prezzo), e sotto, più piccoli, i km e i minuti veri. -->
              <span class="fx-stat fx-stat--fascia">
                <span class="fx-stat__l">Fascia km</span>
                <span class="fx-stat__v">{{ fasciaTesto(pickup_return.simba_km, responseKmReturn, pickup_return.date_partner) }}</span>
                <span
                  v-if="Number(pickup_return.simba_km) > 0"
                  class="fx-stat__sub"
                >{{ percorsoTesto(pickup_return) }}</span>
              </span>

              <span
                class="fx-stat fx-stat--euro"
                :class="{ 'fx-stat--barrata': valetApplicato }"
              >
                <span class="fx-stat__l">Tariffa</span>
                <span class="fx-stat__v">{{ euroTesto(responseCostReturn) }}</span>
              </span>
            </div>
          </el-form>
        </div>

        <div
          v-else
          class="form-card__off"
        >
          Non incluso in questa prenotazione. Accendilo con l'interruttore.
        </div>
      </section>

      <!-- ---------- totale e azioni ----------
           Il totale sta dove si conferma: è l'ultima cosa da controllare. -->
      <div class="form-actions">
        <div class="form-actions__tot">
          <span class="form-actions__totL">Totale</span>
          <s
            v-if="valetApplicato"
            class="pk-was"
          >{{ totaleSenzaValetFormattato }}</s>
          <span class="form-actions__totV">{{ totaleFormattato }}</span>
        </div>
        <span class="form-actions__hint">{{ riepilogoTratte }}</span>
        <div class="form-card__spacer" />
        <ButtonComponent
          label="Annulla"
          :is-router="true"
          link-path="/"
          @button-click="annulla"
        />
        <ButtonComponent
          label="Avanti"
          :is-router="false"
          @button-click="next"
        />
      </div>
    </template>

    <!-- ================= PASSO 2 =================
         Lavori, note e preventivo a sinistra; a destra il riepilogo di
         quello che si sta per creare, perché è l'ultima schermata prima di
         «Crea». I contratti non cambiano: `booking.tasks`
         ({ description, cost, pay }), `booking.note`, i PDF con
         `prenotazioneAPI.upload`. -->
    <template v-else-if="active === 1">
      <div class="lv">
        <div class="lv__main">
          <!-- ---- lavori ---- -->
          <section class="form-card">
            <header class="form-card__head">
              <h3>Lavori di manutenzione</h3>
              <span
                v-if="booking.tasks.length"
                class="lv__count"
              >{{ booking.tasks.length }}</span>
              <div class="form-card__spacer" />
              <span class="form-card__hint">Facoltativi · il cliente li vede nell'app</span>
            </header>

            <div class="form-card__body">
              <div
                v-if="booking.tasks.length"
                class="lv__grid lv__grid--head"
                aria-hidden="true"
              >
                <span>Descrizione</span>
                <span>Costo</span>
                <span>Pagamento</span>
                <span />
              </div>

              <div
                v-for="(task, i) in booking.tasks"
                :key="i"
                class="lv__grid lv__row"
              >
                <el-input
                  v-model="task.description"
                  placeholder="Es. tagliando, cambio pastiglie freni"
                  :class="{ 'is-err': erroreLavoro && erroreLavoro.riga === i && erroreLavoro.campo === 'descrizione' }"
                  :aria-label="`Descrizione del lavoro ${i + 1}`"
                />
                <el-input-number
                  v-model="task.cost"
                  :min="0"
                  :precision="2"
                  :step="10"
                  controls-position="right"
                  class="lv__cost"
                  :class="{ 'is-err': erroreLavoro && erroreLavoro.riga === i && erroreLavoro.campo === 'costo' }"
                  :aria-label="`Costo del lavoro ${i + 1}, in euro`"
                />
                <label class="lv__pay">
                  <el-switch
                    v-model="task.pay"
                    :aria-label="`Il cliente paga il lavoro ${i + 1} nell'app`"
                  />
                  <span>{{ task.pay ? "Paga nell'app" : 'Non in app' }}</span>
                </label>
                <button
                  type="button"
                  class="lv__del"
                  :aria-label="`Elimina il lavoro ${i + 1}`"
                  title="Elimina"
                  @click="rimuoviLavoro(i)"
                >
                  ×
                </button>
              </div>

              <p
                v-if="!booking.tasks.length"
                class="lv__empty"
              >
                Nessun lavoro. Se ne aggiungi, il cliente li trova nell'app e, se vuoi, li paga lì.
              </p>

              <div class="lv__foot">
                <el-button @click="aggiungiLavoro">
                  + Aggiungi un lavoro
                </el-button>
                <span
                  v-if="totaleLavori > 0"
                  class="lv__sum"
                >
                  Lavori <b>{{ euroTesto0(totaleLavori) }}</b>
                  <template v-if="totaleLavoriApp > 0"> · da pagare nell'app {{ euroTesto0(totaleLavoriApp) }}</template>
                </span>
              </div>
              <p
                v-if="erroreLavoro"
                class="lv__err"
                role="alert"
              >
                {{ erroreLavoro.messaggio }}
              </p>
            </div>
          </section>

          <!-- ---- note ---- -->
          <section class="form-card">
            <header class="form-card__head">
              <h3>Note per i driver</h3>
              <div class="form-card__spacer" />
              <span class="form-card__hint">Facoltative</span>
            </header>
            <div class="form-card__body">
              <el-input
                v-model="booking.note"
                type="textarea"
                :rows="3"
                maxlength="1000"
                show-word-limit
                placeholder="Es. chiavi in reception, citofonare a nome Rossi"
                aria-label="Note per i driver"
              />
            </div>
          </section>

          <!-- ---- preventivo ---- -->
          <section class="form-card">
            <header class="form-card__head">
              <h3>Preventivo</h3>
              <div class="form-card__spacer" />
              <span class="form-card__hint">PDF, facoltativo · arriva al cliente per email</span>
            </header>
            <div class="form-card__body">
              <label class="lv__drop">
                <input
                  type="file"
                  accept="application/pdf"
                  multiple
                  class="lv__file"
                  @change="aggiungiPreventivi"
                >
                <span
                  class="lv__dropIcon"
                  aria-hidden="true"
                >⤒</span>
                <span>
                  <b>Scegli i PDF</b> dal computer
                  <small>puoi selezionarne più di uno</small>
                </span>
              </label>
              <ul
                v-if="preventivi.length"
                class="lv__files"
              >
                <li
                  v-for="(f, i) in preventivi"
                  :key="`${f.name}-${i}`"
                  class="lv__fileRow"
                >
                  <span class="lv__fileName">{{ f.name }}</span>
                  <span class="lv__fileSize">{{ dimensioneFile(f.size) }}</span>
                  <button
                    type="button"
                    class="lv__del"
                    :aria-label="`Togli ${f.name}`"
                    @click="preventivi.splice(i, 1)"
                  >
                    ×
                  </button>
                </li>
              </ul>
            </div>
          </section>
        </div>

        <!-- ---- riepilogo ---- -->
        <aside class="lv__aside">
          <section class="form-card">
            <header class="form-card__head">
              <h3>Riepilogo</h3>
            </header>
            <div class="form-card__body lv__summary">
              <div class="lv__kv">
                <span class="lv__k">Cliente</span>
                <span class="lv__v">{{ nomeCliente || '—' }}</span>
              </div>
              <div class="lv__kv">
                <span class="lv__k">Auto</span>
                <span class="lv__v">{{ nomeAuto || '—' }}</span>
              </div>
              <div class="lv__kv">
                <span class="lv__k">Sede</span>
                <span class="lv__v">{{ (booking.headquarter && booking.headquarter.name) || '—' }}</span>
              </div>
              <div
                v-if="is_gone"
                class="lv__kv"
              >
                <span class="lv__k">Andata</span>
                <span class="lv__v">{{ quando(pickup_gone.date_partner) }}</span>
              </div>
              <div
                v-if="is_return"
                class="lv__kv"
              >
                <span class="lv__k">Ritorno</span>
                <span class="lv__v">{{ quando(pickup_return.date_partner) }}</span>
              </div>
              <div
                v-if="valetInUso"
                class="lv__kv"
              >
                <span class="lv__k">Valet</span>
                <span
                  v-if="valetAttivo"
                  class="lv__v lv__v--ok"
                >1 valet · ne restano {{ Math.max(0, (Number(valetInUso.residual_valet) || 0) - 1) }}</span>
                <span
                  v-else
                  class="lv__v"
                >Non applicato</span>
              </div>
              <div class="lv__kv lv__kv--total">
                <span class="lv__k">Corsa</span>
                <span class="lv__v">
                  <s
                    v-if="valetApplicato"
                    class="pk-was"
                  >{{ totaleSenzaValetFormattato }}</s>
                  <b>{{ totaleFormattato }}</b>
                </span>
              </div>
              <div
                v-if="totaleLavori > 0"
                class="lv__kv"
              >
                <span class="lv__k">Lavori</span>
                <span class="lv__v">{{ euroTesto0(totaleLavori) }}</span>
              </div>
            </div>
          </section>
        </aside>
      </div>

      <div class="form-actions">
        <div class="form-actions__tot">
          <span class="form-actions__totL">Totale corsa</span>
          <span class="form-actions__totV">{{ totaleFormattato }}</span>
        </div>
        <span class="form-actions__hint">{{ riepilogoTratte }}</span>
        <div class="form-card__spacer" />
        <el-button @click="back">
          Indietro
        </el-button>
        <el-button
          type="primary"
          :loading="creazione"
          @click="onSubmit"
        >
          Crea prenotazione
        </el-button>
      </div>
    </template>
  </div>
</template>


<script>
import prenotazioneAPI from '../services/prenotazioneAPI.js';
import carAPI from '../services/carAPI.js';
import employeeAPI from '../services/employeeAPI.js';
import customerAPI from '../services/customerAPI';
import store from '../store/prenotazione.js';
import bookingAPI from '@/services/bookingAPI';
import '@vuepic/vue-datepicker/dist/main.css';
import { isLoggedStore } from '../store/logged';
import { useRouter } from 'vue-router';
import { computed, reactive, ref, toRaw, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete } from '@element-plus/icons-vue';
import { isEmpty } from 'lodash';
import AddNewCustomerComponent from './AddNewCustomerComponent.vue';
import { rottaNuovoCliente } from '../services/servizi.js';
import DateSelectComponent from './DateSelectComponent.vue';
import SelectComponent from './SelectComponent.vue';
import PointComponent from './PointComponent.vue';
import ButtonComponent from './ButtonComponent.vue';
import DescriptionComponent from './DescriptionComponent.vue';

import partnerAPI from '../services/partnerAPI.js';
import { normalize } from '../services/crud';

const dayjs = require('dayjs');

export default {
  name: 'PickupComponent',
  components: {
    AddNewCustomerComponent,
    DateSelectComponent,
    PointComponent,
    SelectComponent,
    ButtonComponent,
    DescriptionComponent,
  },

  props: {
    // Data scelta nel calendario dell'Agenda, in forma `YYYY-MM-DD`.
    //
    // Arriva come PROP e non leggendo la rotta: la pagina che ospita questo
    // form la conosce gia', e farla leggere anche da qui significherebbe due
    // posti che devono restare d'accordo su un nome di query string.
    // Vuota se non si arriva dall'Agenda: il calendario parte vuoto.
    dataIniziale: {
      type: String,
      default: '',
    },
    // Arrivo dalla scheda cliente: cliente e auto già scelti.
    // Si applicano appena il cliente è nell'elenco, facendo quello che
    // farebbe l'operatore scegliendoli a mano.
    clienteIniziale: {
      type: String,
      default: '',
    },
    // 'pickup' oppure 'restaurant' (Notturno).
    //
    // È lo STESSO servizio lato dati — il notturno viaggia sulle tratte
    // pickup — ma con un listino diverso, scelto dal server in base all'ora
    // della corsa. Cambia quindi la rotta del preventivo e sparisce l'auto di
    // cortesia, che nel notturno non esiste. Un partner ha l'uno o l'altro,
    // mai tutti e due: lo decide `is_restaurant` (vedi services/servizi.js).
    servizio: {
      type: String,
      default: 'pickup',
    },
    autoIniziale: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const router = useRouter();
    const storeLogin = isLoggedStore();
    const isNotturno = computed(() => props.servizio === 'restaurant');

    // Pulsante «Avanti» disabilitato finché mancano informazioni.
    let button1_disabled = ref(true);

    let loadingSlotsGone = ref(true);
    let loadingSlotsReturn = ref(true);

    const tasks = [
      {
        cost: 0,
        description: '',
        pay: false,
      },
    ];

    let formRulePickup = reactive({
      date_partner: [
        {
          required: true,
          message: 'Inserire date_partner',
          trigger: 'blur',
        },
      ],
    });

    let formRuleBooking = reactive({
      customer: [
        {
          required: true,
          message: 'Inserire customer',
          trigger: 'blur',
        },
      ],
      headquarter: [
        {
          required: true,
          message: 'Inserire headquarter',
          trigger: 'blur',
        },
      ],
    });

    const booking = reactive({
      payed: false,
      imponibile: 0,
      tot_fattura: 0,
      // Niente `rif_fattura` alla creazione: «non fatturato» è NULL, e 0
      // verrebbe salvato come un riferimento vero.
      liquidato: false,
      data: new Date(),
      over_twentysix: false,
      loaner: 'false',
      targa_loaner: '',
      state: 'before',
      type: 'both',
      note: '',
      // Il server lo riscrive comunque dal partner (`is_restaurant` →
      // 'restaurant'), ma mandarlo giusto tiene la richiesta leggibile.
      type_booking: props.servizio === 'restaurant' ? 'restaurant' : 'booking',
      customer: store.customerSelectedObj,
      car: {},
      employee: storeLogin.employee,
      headquarter: store.heaquarterSelectedObj,
      tasks: [
        {
          description: '',
          cost: 0,
          pay: false,
          accepted: false,
        },
      ],
      type_prenotazione: 'pickup',

      pickup_bookings: [],
    });

    const pickup_gone = reactive({
      date_partner: '',
      new_date_partner: null,
      date_customer: '',
      date_driver: '',
      date_jolly_scan: '',
      true_date_partner: '',
      true_date_customer: '',
      true_date_driver: '',
      simba_km: 0,
      simba_km_range: 0,
      simba_imponibile: 0.0,
      simba_minute: 0,
      type_booking: 'gone',
      order: 0,
      qrCode: '',
      driver_pickup: null,
      planner: null,
      point: {
        address: store.originPointAddress, //"",
        street_number: store.originPointStreetNumber, //",
        cap: store.originPointCity, //"",
        city: store.originPointCap, //"",
        province: store.originPointProvince, //"",
        escort_bookings: null,
        delivery_bookings: null,
        pickup_bookings: null,
        label: '',
        latitude: 0,
        longitude: 0,
      },
      planners: [],
    });

    const pickup_return = reactive({
      date_partner: '',
      date_customer: '',
      date_driver: '',
      date_jolly_scan: '',
      true_date_partner: '',
      true_date_customer: '',
      true_date_driver: '',
      simba_km: 0,
      simba_km_range: 0,
      simba_imponibile: 0.0,
      simba_minute: 0,
      type_booking: 'return',
      order: 0,
      qrCode: '',
      driver_pickup: null,
      planner: null,
      point: {
        address: store.destinationPointAddress,
        street_number: store.destinationPointStreetNumber,
        cap: store.destinationPointCity,
        city: store.destinationPointCap,
        province: store.destinationPointProvince,
        escort_bookings: null,
        delivery_bookings: null,
        pickup_bookings: null,
        label: '',
        latitude: 0,
        longitude: 0,
      },
      planners: [],
    });

    let customerExist = ref('');

    const checkCustomerExist = async () => {
      try {
        let request = {
          mail: customerExist.value,
        };

        const response = await customerAPI.checkCustomerExist(request);

        if (response.data.exist == true) {
          ElMessageBox.confirm(
            `Nome: ${response.data.customer.name}<br>Cognome: ${response.data.customer.surname}<br>Email: ${response.data.customer.mail}`,
            'Cliente Trovato',
            {
              dangerouslyUseHTMLString: true,
              confirmButtonText: 'Aggiungi il cliente',
              cancelButtonText: 'Annulla',
            }
          )
            .then(async () => {
              await addPartnerToCustomer();
            })
            .catch(() => {});
        } else {
          ElMessageBox.confirm(
            'Il cliente non esiste, registralo prima di prenotare',
            {
              confirmButtonText: 'Registra un cliente',
              cancelButtonText: 'Cancel',
              type: 'warning',
              customClass: 'custom-message-box',
            }
          )
            .then(() => {
              router.push(rottaNuovoCliente('pickup', router.currentRoute.value.query.data, customerExist.value));
            })
            .catch(() => {});
        }
      } catch (error) {
        ElMessage({
          message: 'Si è verificato un errore durante la verifica del cliente',
          type: 'error',
        });
        console.error(error);
      }
    };
    //cliente
    let customerSelected = ref(store.customerSelected);
    const customersId = store.customersCascader;
    let customers = store.customers;

    const addPartnerToCustomer = async () => {
      const responsePartner = await partnerAPI.getPartnerByEmployee(
        storeLogin.id
      );

      const partner = responsePartner.data[0].headquarters[0].partner;

      try {
        let request = {
          mail: customerExist.value,
          partner: partner,
        };

        const response = await customerAPI.addPartnerToCustomer(request);

        customerSelected.value = response.data.customer.id;

        booking.customer = response.data.customer;

        store.customerSelectedObj = response.data.customer;

        const customerExistsInStore = customersId.some(
          (customer) => customer.value === response.data.customer.id
        );

        if (!customerExistsInStore) {
          store.customers.push(response.data.customer);
        }

        let tableRow = {
          value: booking.customer.id,
          label: `${booking.customer.name} ${booking.customer.surname}, ${booking.customer.mail}`,
        };

        if (!customerExistsInStore) {
          store.customersCascader.push(tableRow);
        }

      } catch (error) {
        console.error(error);
      }
    };

    const handleChangeCustomer = async (val) => {
      customerSelected.value = val ? val : booking.customer.id;
      carsId.value = [];

      booking.customer = customers.filter((customer) => customer.id == val)[0];

      store.customerSelected = booking.customer.id;
      store.customerSelectedObj = booking.customer;

      pickup_gone.point.address = booking.customer.address;
      pickup_gone.point.street_number = booking.customer.street_number;
      pickup_gone.point.cap = booking.customer.cap;
      pickup_gone.point.city = booking.customer.city;
      pickup_gone.point.province = booking.customer.province;

      store.originPointAddress = pickup_gone.point.address;
      store.originPointStreetNumber = pickup_gone.point.street_number;
      store.originPointCity = pickup_gone.point.cap;
      store.originPointCap = pickup_gone.point.city;
      store.originPointProvince = pickup_gone.point.province;

      pickup_return.point.address = booking.customer.address;
      pickup_return.point.street_number = booking.customer.street_number;
      pickup_return.point.cap = booking.customer.cap;
      pickup_return.point.city = booking.customer.city;
      pickup_return.point.province = booking.customer.province;

      let resposnePointGone = await loadPoints(pickup_gone.point);

      pickup_gone.point.label = resposnePointGone[0].label;
      pickup_gone.point.latitude = resposnePointGone[0].point.latitude;
      pickup_gone.point.longitude = resposnePointGone[0].point.longitude;

      let resposnePoinReturn = await loadPoints(pickup_return.point);

      pickup_return.point.label = resposnePoinReturn[0].label;
      pickup_return.point.latitude = resposnePoinReturn[0].point.latitude;
      pickup_return.point.longitude = resposnePoinReturn[0].point.longitude;

      store.destinationPointAddress = pickup_return.point.address;
      store.destinationPointStreetNumber = pickup_return.point.street_number;
      store.destinationPointCity = pickup_return.point.cap;
      store.destinationPointCap = pickup_return.point.city;
      store.destinationPointProvince = pickup_return.point.province;

      if (typeof val === 'object' && !Array.isArray(val) && val !== null) {
        await loadCars(val[0]);
      } else {
        await loadCars(val);
      }

      isAvantiButtonDisabled();
      calcolaKmGone();
      calcolaKmReturn();
    };
    //macchina
    let carSelected = ref(store.carSelected);
    let carsId = ref([]);
    const cars = ref([]);

    const loadCars = async (customer_id) => {
      try {

        const response = await carAPI.getCarsByCustomer(
          customer_id /* , partner.data[0].headquarters[0].partner.id */
        );

        // Ogni auto del cliente UNA volta sola, anche con più pacchetti o con
        // pacchetti da confermare: il valet usabile lo mostra `loadMinutes`.
        const elenco = response.data || [];
        cars.value = elenco;
        carsId.value = elenco.map((car) => ({
          value: car.id,
          label: [car.marca, car.modello, car.targa].filter(Boolean).join(' '),
        }));
      } catch (error) {
        console.error(error);
      }
    };

    const pointToString = (point) => {
      const address = point.address ? point.address : '';
      const street_number = point.street_number ? point.street_number : '';
      const city = point.city ? point.city : '';
      const province = point.province ? point.province : '';
      const cap = point.cap ? point.cap : '';

      return (
        address + ' ' + street_number + ' ' + cap + ', ' + city + ' ' + province
      );
    };

    let loadingPoint = ref(true);

    const loadPoints = async (point) => {
      ///e.log("🚀 ~ loadPoints ~ point:", point)
      try {
        const response = await prenotazioneAPI.getPoints({
          text: pointToString(point),
        });

        loadingPoint.value = false;
        return response.data;
      } catch (error) {
        console.error(error);
        return [];
      }
    };

    const handleChangeCar = async (val) => {
      const auto = cars.value.filter((car) => car.id == val)[0];
      if (!auto) return;
      booking.car = auto;
      carSelected.value = booking.car.id;
      // Pacchetti dell'auto di prima: via subito, prima di rileggerli.
      pacchettiUsabili.value = [];
      minuteCar.value = {};
      isAvantiButtonDisabled();
      loadMinutes(booking.headquarter.id);
    };

    //sede
    let headquarterSelected = ref(store.heaquarterSelected);
    let headquartersId = store.headquartersCascader;

    const headquarters = store.headquarters;

    const handleChangeHeadquarter = async (val) => {
      headquarterSelected.value = val;
      booking.headquarter = headquarters.filter(
        (headquarter) => headquarter.id == val
      )[0];

      store.heaquarterSelected = booking.headquarter.id;
      store.heaquarterSelectedObj = booking.headquarter;

      calcolaKmGone();
      calcolaKmReturn();

      isAvantiButtonDisabled();
      loadMinutes(toRaw(val[0]));
    };

    let show_minute = true;
    let residual_minuteVal = ref(0);
    let minuteCar = ref({});
    // Tutti i pacchetti usabili dell'auto, il primo è quello che userà il server.
    const pacchettiUsabili = ref([]);

    const loadMinutes = async (headquartersId) => {
      try {
        if (headquartersId && booking.car.id) {
          const partner_response = await employeeAPI.getPartnerByHeadquarter(
            headquartersId
          );
          const response = await customerAPI.getMinuteByCar(
            booking.car.id,
            partner_response.data[0].partner.id
          );
          // Il pacchetto lo sceglie il SERVER alla creazione del booking: qui si
          // mostra quello che userà — confermato, con residui, non scaduto,
          // quello che scade prima — e nient'altro.
          const oggi = dayjs().startOf('day');
          const usabili = (response.data || [])
            .filter((m) => m.accepted === true
              && Number(m.residual_valet) > 0
              && m.expiration_valet
              && !dayjs(m.expiration_valet).isBefore(oggi, 'day'))
            .sort((a, b) => new Date(a.expiration_valet) - new Date(b.expiration_valet));

          pacchettiUsabili.value = usabili;
          if (!usabili.length) {
            show_minute = false;
            residual_minuteVal.value = 0;
            minuteCar.value = {};
          } else {
            minuteCar.value = usabili[0];
            residual_minuteVal.value = usabili[0].residual_valet;
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    //scelta di solo andata, solo ritorno o entrambi
    let is_gone = ref(true);
    let is_return = ref(true);

    const isAvantiButtonDisabled = () => {
      const pickupGone = pickup_gone.point;

      const pickupReturn = pickup_return.point;

      if (is_return.value && is_gone.value) {
        if (
          (pickupGone.address == '' &&
            pickupGone.street_number == '' &&
            pickupGone.city == '' &&
            pickupGone.province == '' &&
            pickupGone.cap == '') ||
          (pickupReturn.address == '' &&
            pickupReturn.street_number == '' &&
            pickupReturn.city !== '' &&
            pickupReturn.province == '' &&
            pickupReturn.cap == '') ||
          isEmpty(toRaw(booking.customer)) ||
          isEmpty(toRaw(booking.car)) ||
          isEmpty(toRaw(booking.headquarter)) ||
          pickup_gone.date_partner == '' ||
          pickup_return.date_partner == ''
        ) {
          button1_disabled.value = true;
        } else {
          button1_disabled.value = false;
        }
      } else if (!is_return.value && is_gone.value) {
        if (
          (pickupGone.address == '' &&
            pickupGone.street_number == '' &&
            pickupGone.city == '' &&
            pickupGone.province == '' &&
            pickupGone.cap == '') ||
          isEmpty(toRaw(booking.customer)) ||
          isEmpty(toRaw(booking.car)) ||
          isEmpty(toRaw(booking.headquarter)) ||
          pickup_gone.date_partner == ''
        ) {
          button1_disabled.value = true;
        } else {
          button1_disabled.value = false;
        }
      } else if (is_return.value && !is_gone.value) {
        if (
          (pickupReturn.address == '' &&
            pickupReturn.street_number == '' &&
            pickupReturn.city == '' &&
            pickupReturn.province == '' &&
            pickupReturn.cap == '') ||
          isEmpty(toRaw(booking.customer)) ||
          isEmpty(toRaw(booking.car)) ||
          isEmpty(toRaw(booking.headquarter)) ||
          pickup_return.date_partner == ''
        ) {
          button1_disabled.value = true;
        } else {
          button1_disabled.value = false;
        }
      }
      return true;
    };

    const deleteGone = () => {
      is_gone.value = !is_gone.value;
      isAvantiButtonDisabled();
    };

    const deleteReturn = () => {
      is_return.value = !is_return.value;
      isAvantiButtonDisabled();
    };

    // ORARI PROPONIBILI, uno ogni quarto d'ora.
    //
    // Il pickup ordinario si ferma alle 18:45, come da sempre. Il NOTTURNO no:
    // è un servizio serale e notturno, e con quella finestra non si poteva
    // prenotare l'ora in cui il servizio esiste. Per lui si coprono tutte le
    // 24 ore — il server propone comunque solo gli slot con un driver libero,
    // e il listino serale (22–06) lo sceglie da sé.
    const slotDellaGiornata = () => {
      const dal = props.servizio === 'restaurant' ? 0 : 8 * 60 + 45;
      const al = props.servizio === 'restaurant' ? 23 * 60 + 45 : 18 * 60 + 45;
      const slot = [];
      for (let m = dal; m <= al; m += 15) {
        const ora = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
        slot.push({ value: ora, label: ora });
      }
      return slot;
    };

    //ottengo slot disponibili
    const selectedSlotGone = ref(null);
    const selectedSlotReturn = ref(null);
    const timeSlotsGone = ref(slotDellaGiornata());
    const timeSlotsReturn = ref(slotDellaGiornata());

    const handleChangeDateGone = async (val) => {
      try {
        pickup_gone.date_partner = val;
        await loadSlotGone(
          dayjs(pickup_gone.date_partner).format('YYYY-MM-DD')
        );
      } catch (error) {
        console.error(error);
      }
    };

    const handleChangeDateReturn = async (val) => {
      try {
        pickup_return.date_partner = val;
        const selectedDate = dayjs(pickup_return.date_partner).format(
          'YYYY-MM-DD'
        );
        await loadSlotReturn(selectedDate);
      } catch (error) {
        console.error(error);
      }
    };

    let request_slot_gone = {
      originPoint: {},
      destinationPoint: {},
      partnerDay: new Date(),
      type: '',
      area: '',
    };

    const SlotGone = async (date) => {
      loadingSlotsGone.value = true;
      let newSlot = '';
      handleSlotChangeGone(newSlot);
      try {
        if (
          !isEmpty(toRaw(booking.headquarter)) &&
          !isEmpty(toRaw(pickup_gone.point)) &&
          date != ''
        ) {
          if (booking.loaner == 'false') {
            request_slot_gone.originPoint = toRaw(booking.headquarter);
            request_slot_gone.destinationPoint = toRaw(pickup_gone.point);
            request_slot_gone.partnerDay = date;
            request_slot_gone.type = 'PICKUP_GONE';
          } else {
            request_slot_gone.originPoint = toRaw(booking.headquarter);
            request_slot_gone.destinationPoint = toRaw(pickup_gone.point);
            request_slot_gone.partnerDay = date;
            request_slot_gone.type = 'PICKUP_AUTO_GONE';
          }

          let response = null;
          request_slot_gone.area = booking.headquarter.area;
          request_slot_gone.headquarterId = booking.headquarter.id;

          response = await prenotazioneAPI.calcSlot(request_slot_gone);

          if (response) {
            loadingSlotsGone.value = false;
          }
          return response.data;
        }
      } catch (error) {
        console.error('Error loading slots:', error);
        return null;
      }
    };

    let request_slot_return = {
      originPoint: {},
      destinationPoint: {},
      partnerDay: new Date(),
      type: '',
      area: '',
    };

    const SlotReturn = async (date) => {
      loadingSlotsReturn.value = true;
      let newSlot = '';
      handleSlotChangeReturn(newSlot);
      try {
        if (
          !isEmpty(toRaw(booking.headquarter)) &&
          !isEmpty(toRaw(pickup_return.point))
        ) {
          if (booking.loaner == 'false') {
            request_slot_return.originPoint = toRaw(booking.headquarter);
            request_slot_return.destinationPoint = toRaw(pickup_return.point);
            request_slot_return.type = 'PICKUP_RETURN';
            request_slot_return.partnerDay = date;
          } else {
            request_slot_return.originPoint = toRaw(booking.headquarter);
            request_slot_return.destinationPoint = toRaw(pickup_return.point);
            request_slot_return.type = 'PICKUP_AUTO_RETURN';
            request_slot_return.partnerDay = date;
          }
          request_slot_return.area = booking.headquarter.area;
          request_slot_return.headquarterId = booking.headquarter.id;
          let response_slot = await prenotazioneAPI.calcSlot(
            request_slot_return
          );
          loadingSlotsReturn.value = false;
          return response_slot.data;
        }
      } catch (error) {
        console.error('Error loading slots:', error);
        return null;
      }
    };

    const mergeSlotsGone = async (timeSlots, selectedDate) => {
      // 1: carico slot disponibili dal backend
      const availableSlots = await SlotGone(selectedDate);
      loadingSlotsGone.value = false;
      if (availableSlots) {
        // 2: seleziono gli slot disponibili
        const mergedSlots = timeSlots.map((timeSlot) => {
          const isAvailable = availableSlots.includes(timeSlot.value);

          return {
            ...timeSlot,
            disabled: !isAvailable,
          };
        });
        return mergedSlots;
      }
      return [];
    };

    const mergeSlotsReturn = async (timeSlots, selectedDate) => {
      // 1: carico slot disponibili dal backend
      const availableSlots = await SlotReturn(selectedDate);
      loadingSlotsReturn.value = false;
      if (availableSlots) {
        // 2: seleziono gli slot disponibili
        const mergedSlots = timeSlots.map((timeSlot) => {
          let isAvailable = availableSlots.includes(timeSlot.value);

          const [hour, minute] = timeSlot.value.split(':').map(Number);

          const newDate = dayjs(selectedDate)
            .hour(hour)
            .minute(minute)
            .second(0)
            .toDate();

          isAvailable =
            isAvailable && is_gone.value
              ? new Date(newDate).getTime() >
                new Date(pickup_gone.date_partner).getTime()
              : isAvailable;

          return {
            ...timeSlot,
            disabled: !isAvailable,
          };
        });
        return mergedSlots;
      }
      return [];
    };

    const loadSlotGone = async (selectedDate) => {
      if (selectedDate != '') {
        const mergedSlotsGone = await mergeSlotsGone(
          timeSlotsGone.value,
          selectedDate
        );

        timeSlotsGone.value.splice(
          0,
          timeSlotsGone.value.length,
          ...mergedSlotsGone
        );
      }
    };

    const loadSlotReturn = async (selectedDate) => {
      if (selectedDate != '') {
        const mergedSlotsReturn = await mergeSlotsReturn(
          timeSlotsReturn.value,
          selectedDate
        );
        timeSlotsReturn.value.splice(
          0,
          timeSlotsReturn.value.length,
          ...mergedSlotsReturn
        );
      }
    };

    const handleSlotChangeGone = (value) => {
      selectedSlotGone.value = value;
      if (value != '') {
        pickup_gone.date_partner = dayjs(new Date(pickup_gone.date_partner))
          .hour(value[0].split(':')[0])
          .minute(value[0].split(':')[1])
          .second(0)
          .toISOString();
      } else {
        return '';
      }

      // Nel notturno l'ORA decide il listino (serale o diurno): scelto lo
      // slot, il prezzo va rifatto. Nel pickup dipende solo dai km.
      if (isNotturno.value) calcolaKmGone();

      isAvantiButtonDisabled();
    };

    const handleSlotChangeReturn = (value) => {
      selectedSlotReturn.value = value;

      if (value != '') {
        pickup_return.date_partner = dayjs(new Date(pickup_return.date_partner))
          .hour(value[0].split(':')[0])
          .minute(value[0].split(':')[1])
          .second(0)
          .toISOString();
      } else {
        return '';
      }

      if (isNotturno.value) calcolaKmReturn();

      isAvantiButtonDisabled();
    };

    //scelta dell'auto di cortesia da fornire al cliente

    const changeLoaner = (val) => {
      booking.loaner = val;

      if (is_gone.value && is_return.value) {
        calcolaKmGone();
        calcolaKmReturn();
        selectedSlotGone.value = null;
        selectedSlotReturn.value = null;
        if (pickup_gone.date_partner != '')
          loadSlotGone(pickup_gone.date_partner);
        if (pickup_return.date_partner != '')
          loadSlotReturn(pickup_return.date_partner);
      } else if (is_gone.value && !is_return.value) {
        calcolaKmGone();
        selectedSlotGone.value = null;
        if (pickup_gone.date_partner != '')
          loadSlotGone(pickup_gone.date_partner);
      } else if (!is_gone.value && is_return.value) {
        calcolaKmReturn();
        selectedSlotReturn.value = null;
        if (pickup_return.date_partner != '')
          loadSlotReturn(pickup_return.date_partner);
      }
    };

    let button2_disabled = ref(true);

    //gestione step della prenotazione
    let active = ref(0);

    let highlightCustomer = ref(false);
    let highlightHeadquarter = ref(false);
    let highlightCar = ref(false);

    let highlightDateGone = ref(false);
    let highlightDateReturn = ref(false);
    let highlightSlotGone = ref(false);
    let highlightSlotReturn = ref(false);

    const next = () => {
      if (!customerSelected.value) {
        ElMessage.error('Perfavore inserisci un cliente');
        highlightCustomer.value = true;
        return;
      }

      if (!Object.keys(booking.car).length) {
        ElMessage.error('Perfavore inserisci auto');
        highlightCar.value = true;
        return;
      }

      if (!headquarterSelected.value) {
        ElMessage.error('Perfavore inserisci una sede');
        highlightHeadquarter.value = true;
        return;
      }

      if (is_gone.value && pickup_gone.date_partner == '') {
        ElMessage.error('Perfavore inserisci una data di ritiro auto');
        highlightDateGone.value = true;
        return;
      }

      if (is_gone.value && !selectedSlotGone.value) {
        ElMessage.error('Perfavore inserisci un orario');
        highlightSlotGone.value = true;
        return;
      }

      if (is_return.value && pickup_return.date_partner == '') {
        ElMessage.error('Perfavore inserisci una data di consegna auto');
        highlightDateReturn.value = true;
        return;
      }

      if (is_return.value && !selectedSlotReturn.value) {
        ElMessage.error('Perfavore inserisci un orario');
        highlightSlotReturn.value = true;
        return;
      }

      highlightCustomer.value = false;
      highlightHeadquarter.value = false;
      highlightDateGone.value = false;
      highlightDateReturn.value = false;
      highlightSlotGone.value = false;
      highlightSlotReturn.value = false;

      if (active.value++ > 1) {
        active.value = 0;
      }
    };

    // Preventivi in PDF. Si tengono i File in una lista, così si possono
    // togliere prima di creare; il FormData si costruisce solo all'invio.
    const preventivi = ref([]);
    const aggiungiPreventivi = (evento) => {
      const scelti = Array.from(evento.target.files || []);
      evento.target.value = '';
      const nonPdf = scelti.filter((f) => f.type !== 'application/pdf');
      if (nonPdf.length) {
        ElMessage({ type: 'warning', message: `Solo PDF: ${nonPdf.map((f) => f.name).join(', ')} non aggiunto.` });
      }
      preventivi.value.push(...scelti.filter((f) => f.type === 'application/pdf'));
    };
    const dimensioneFile = (b) => {
      if (!b) return '';
      if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
      return `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
    };

    const fileLoad = async (fd) => {
      try {
        const mail = booking.customer.mail;
        await prenotazioneAPI.upload(fd, mail);
      } catch (e) {
        console.error(e);
      }
    };

    //range del booking
    let responseKmGone = ref(store.rangeKmGonePickup);
    let responseKmReturn = ref(store.rangeKmReturnPickup);
    let responseCostGone = ref(store.rangeCostGonePickup);
    let responseCostReturn = ref(store.rangeCostReturnPickup);

    // -------------------------------------------------------------------
    //  TOTALE E RIEPILOGO: si sommano solo le tratte davvero incluse,
    //  perché è quello che verrà fatturato.
    //
    //  ⚠️ `Number(...)` con ripiego a 0: quei ref nascono dallo store e
    //  possono contenere stringhe o valori non ancora calcolati. Senza la
    //  conversione «12» + «20» darebbe «1220».
    // -------------------------------------------------------------------
    const importo = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    // Somma delle tariffe delle tratte incluse, SENZA valet.
    const totaleSenzaValet = computed(
      () => (is_gone.value ? importo(responseCostGone.value) : 0)
        + (is_return.value ? importo(responseCostReturn.value) : 0)
    );

    const euroIt = (n) => `${importo(n).toFixed(2).replace('.', ',')} €`;
    const numeroIt = (v) => importo(v).toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const euroTesto0 = (v) => euroIt(v);

    // -------------------------------------------------------------------
    //  FASCIA KM: quella del listino del partner in cui rientrano i km veri.
    //
    //  `kmRange` del backend è solo il limite superiore della fascia (e, se la
    //  fascia non c'è, i km stessi): per dire «10–15 km» servono anche gli
    //  estremi, che dà il listino (`/tariff-band/partner-tariffs`, lo stesso
    //  della pagina Tariffe). Confini come nel backend (`resolveBand`):
    //  `max_inclusive` false = [min, max), true = (min, max].
    // -------------------------------------------------------------------
    // Nel notturno i listini sono altri due: `restaurant` e, per le corse fra
    // le 22 e le 6, `restaurant_night`. Sono gli stessi che sceglie il server.
    const LISTINI = props.servizio === 'restaurant'
      ? ['restaurant', 'restaurant_night']
      : ['pickup', 'pickup_loaner'];
    const fascePickup = ref(Object.fromEntries(LISTINI.map((x) => [x, []])));
    const caricaFasce = async () => {
      try {
        const res = await partnerAPI.getPartnerTariffs();
        const gruppi = res?.data?.groups || [];
        LISTINI.forEach((servizio) => {
          const g = gruppi.find((x) => x.service === servizio && (x.rows || []).length);
          fascePickup.value[servizio] = g ? g.rows : [];
        });
      } catch (e) {
        console.warn('Listino fasce non letto:', e?.response?.status);
      }
    };
    caricaFasce();

    const rientraInFascia = (f, km) => {
      const min = Number(f.min_km) || 0;
      const max = f.max_km === null || f.max_km === undefined ? null : Number(f.max_km);
      return f.max_inclusive
        ? (min === 0 || km > min) && (max === null || km <= max)
        : min <= km && (max === null || km < max);
    };

    // Fra le 22 e le 6 il notturno ha il suo listino: stessa soglia del server.
    const isOraNotturna = (quando) => {
      if (!quando) return false;
      const h = new Date(quando).getHours();
      return h >= 22 || h < 6;
    };

    const fasciaTesto = (kmVeri, kmRange, quando = null) => {
      const km = importo(kmVeri);
      if (km > 0) {
        let servizio;
        if (isNotturno.value) {
          servizio = isOraNotturna(quando) ? 'restaurant_night' : 'restaurant';
        } else {
          servizio = String(booking.loaner).toLowerCase() === 'true' ? 'pickup_loaner' : 'pickup';
        }
        const f = (fascePickup.value[servizio] || []).find((x) => rientraInFascia(x, km));
        if (f) {
          if (f.max_km === null || f.max_km === undefined) return `oltre ${numeroIt(f.min_km)} km`;
          if (!Number(f.min_km)) return `fino a ${numeroIt(f.max_km)} km`;
          return `${numeroIt(f.min_km)}–${numeroIt(f.max_km)} km`;
        }
      }
      // Senza listino: il limite superiore dato dal backend, se è un limite.
      const limite = importo(kmRange);
      return limite > 0 && limite !== km ? `fino a ${numeroIt(limite)} km` : '—';
    };

    const percorsoTesto = (tratta) => {
      const parti = [`${numeroIt(tratta.simba_km)} km`];
      if (importo(tratta.simba_minute) > 0) parti.push(`${Math.round(importo(tratta.simba_minute))} min`);
      return parti.join(' · ');
    };

    // -------------------------------------------------------------------
    //  VALET: quale pacchetto userà il server e quanto costerà la corsa.
    //
    //  Stessa regola di `BookingService.consumeValetOnCreate`:
    //   - pacchetto confermato, con residui, valido il giorno della corsa,
    //     quello che scade prima;
    //   - pacchetto storico (senza `valet_per_year`) o senza km compresi:
    //     corsa gratis;
    //   - altrimenti si pagano i km di OGNI tratta (andata e ritorno) oltre
    //     `included_km`, al €/km della fascia valet. Qui con quello del
    //     venditore, come la «Tariffa» delle tratte: quello copiato sul
    //     pacchetto, o quello di oggi della sua fascia se sul pacchetto non c'è.
    // -------------------------------------------------------------------
    const giornoServizio = computed(() => {
      const d = is_gone.value ? pickup_gone.date_partner : pickup_return.date_partner;
      const giorno = d ? dayjs(d) : null;
      const oggi = dayjs().startOf('day');
      return giorno && giorno.isValid() && giorno.isAfter(oggi) ? giorno.startOf('day') : oggi;
    });

    const valetInUso = computed(() => {
      if (!booking.car?.id || (!is_gone.value && !is_return.value)) return null;
      return pacchettiUsabili.value.find(
        (m) => !dayjs(m.expiration_valet).isBefore(giornoServizio.value, 'day')
      ) || null;
    });

    // I km delle tratte accese: ognuna si confronta con i km compresi.
    const kmTratteValet = computed(() => [
      ...(is_gone.value ? [importo(pickup_gone.simba_km)] : []),
      ...(is_return.value ? [importo(pickup_return.simba_km)] : []),
    ]);

    // €/km del venditore: quello copiato sul pacchetto; se manca, quello di oggi
    // della fascia da cui è stato venduto. Le fasce si chiedono una volta sola.
    // Stessa ricerca del backend (`ValetService.bandOfPackage`): con
    // `valet_band_id` è quella, altrimenti dai km compresi. Le fasce generali
    // di Simba non hanno prezzi del venditore.
    let fasceValet = null;
    const tariffaKmValet = async (m) => {
      const suo = m.partner_excess_price_per_km;
      if (suo !== null && suo !== undefined && suo !== '') return Number(suo);
      if (!fasceValet) {
        fasceValet = partnerAPI.getValetBands()
          .then((res) => normalize(res).rows.filter((f) => f.partnerId && f.partnerId === storeLogin.partner?.id))
          .catch((e) => {
            fasceValet = null;
            throw e;
          });
      }
      const fasce = await fasceValet;
      const compresi = Number(m.included_km);
      const fascia = (m.valet_band_id && fasce.find((f) => f.id === m.valet_band_id))
        || fasce.find((f) => f.to_km !== null && Number(f.to_km) === compresi)
        || fasce.find((f) => f.to_km === null && compresi >= Number(f.from_km));
      const v = fascia?.partner_excess_price_per_km;
      return v === null || v === undefined || v === '' ? null : Number(v);
    };

    const stimaValet = reactive({ stato: 'attesa', tratte: 0, kmEccedenza: 0, prezzoKm: null, prezzo: null });
    let richiestaValet = 0;
    const ricalcolaValet = async () => {
      const mia = ++richiestaValet;
      const m = valetInUso.value;
      const tratte = kmTratteValet.value;
      stimaValet.tratte = tratte.length;
      if (!m) {
        stimaValet.stato = 'attesa';
        return;
      }
      if (m.valet_per_year == null || m.included_km == null) {
        Object.assign(stimaValet, { stato: 'ok', kmEccedenza: 0, prezzo: 0 });
        return;
      }
      if (!tratte.length || tratte.some((km) => !(km > 0))) {
        Object.assign(stimaValet, { stato: 'attesa', kmEccedenza: 0, prezzo: null });
        return;
      }
      const compresi = Number(m.included_km);
      const eccedenza = Math.round(tratte.reduce((t, km) => t + Math.max(0, km - compresi), 0) * 100) / 100;
      if (!eccedenza) {
        Object.assign(stimaValet, { stato: 'ok', kmEccedenza: 0, prezzo: 0 });
        return;
      }
      Object.assign(stimaValet, { stato: 'calcolo', kmEccedenza: eccedenza, prezzo: null });
      try {
        const prezzoKm = await tariffaKmValet(m);
        if (mia !== richiestaValet) return;
        Object.assign(stimaValet, prezzoKm === null || !Number.isFinite(prezzoKm)
          ? { stato: 'mancante', prezzoKm: null, prezzo: null }
          : { stato: 'ok', prezzoKm, prezzo: Math.round(eccedenza * prezzoKm * 100) / 100 });
      } catch (e) {
        if (mia !== richiestaValet) return;
        console.warn('Costo con il valet non calcolato:', e?.response?.status);
        Object.assign(stimaValet, { stato: 'errore', prezzo: null });
      }
    };
    watch(
      () => [valetInUso.value?.id, kmTratteValet.value.join('|'), giornoServizio.value.valueOf()],
      ricalcolaValet,
      { immediate: true }
    );

    // Valet facoltativo: acceso di default, come il backend. Torna acceso a
    // ogni cambio di pacchetto (auto diversa): la scelta vale per quel pacchetto.
    const applicaValet = ref(true);
    watch(() => valetInUso.value?.id, () => {
      applicaValet.value = true;
    });
    const valetAttivo = computed(() => !!valetInUso.value && applicaValet.value);
    const valetApplicato = computed(() => valetAttivo.value && stimaValet.stato === 'ok');

    // Il totale che si vede: con il valet, quello che resta da pagare; se il
    // costo con il valet non si conosce ancora, «—» invece di un numero sbagliato.
    const totaleFormattato = computed(() => {
      if (!valetAttivo.value) return euroIt(totaleSenzaValet.value);
      return stimaValet.stato === 'ok' ? euroIt(stimaValet.prezzo) : '—';
    });
    const totaleSenzaValetFormattato = computed(() => euroIt(totaleSenzaValet.value));

    const percentualeValet = (m) => {
      const tot = importo(m?.valet);
      return tot > 0 ? Math.max(0, Math.min(100, Math.round((importo(m.residual_valet) / tot) * 100))) : 0;
    };
    const dataBreve = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');

    const riepilogoTratte = computed(() => {
      const tratte = [];
      if (is_gone.value) tratte.push('andata');
      if (is_return.value) tratte.push('ritorno');
      if (!tratte.length) return 'Nessuna tratta inclusa';
      const km = (is_gone.value ? importo(pickup_gone.simba_km) : 0)
        + (is_return.value ? importo(pickup_return.simba_km) : 0);
      const parte = tratte.join(' e ');
      const testo = `${parte.charAt(0).toUpperCase()}${parte.slice(1)}`;
      const conKm = km > 0 ? `${testo} · ${numeroIt(km)} km` : testo;
      return valetAttivo.value ? `${conKm} · con valet` : conKm;
    });

    // Tariffa di una tratta: finché non c'è un numero vero si scrive «—».
    const euroTesto = (v) => {
      const n = importo(v);
      return n > 0 ? euroIt(n) : '—';
    };

    let request = {
      headquarter_id: '',
      origins_point: {},
      destination_point: {},
      loaner: false,
    };

    const calcolaKmGone = async () => {
      try {
        request.headquarter_id = booking.headquarter.id;
        request.origins_point = toRaw(pickup_gone.point);
        request.destination_point = toRaw(booking.headquarter);

        request.loaner = booking.loaner.toLowerCase() === 'true';
        // Notturno: il listino dipende dall'ora della corsa, quindi la data
        // fa parte della domanda. Senza slot scelto non si chiede il prezzo:
        // il server non saprebbe se è fascia serale o diurna.
        request.date = pickup_gone.date_partner || null;

        if (
          !isEmpty(toRaw(booking.headquarter))
        ) {
          if (isNotturno.value && !request.date) return;
          const response = isNotturno.value
            ? await bookingAPI.getRangeRestaurant(request)
            : await bookingAPI.getRangePickup(request);

          pickup_gone.simba_minute = response.data.minute;

          pickup_gone.simba_km = response.data.km;
          pickup_gone.simba_imponibile = response.data.imponibile;
          pickup_gone.simba_km_range = response.data.kmRange;

          responseKmGone.value = response.data.kmRange;
          responseCostGone.value = response.data.cost;

          store.rangeKmGonePickup = response.data.kmRange;
          store.rangeCostGonePickup = response.data.cost;
        }
      } catch (error) {
        console.error(error);
      }
    };

    calcolaKmGone();

    const calcolaKmReturn = async () => {
      request.headquarter_id = booking.headquarter.id;
      request.origins_point = toRaw(booking.headquarter);
      request.destination_point = toRaw(pickup_return.point);
      request.loaner = booking.loaner.toLowerCase() === 'true';
      request.date = pickup_return.date_partner || null;

      if (
        !isEmpty(toRaw(booking.headquarter))
      ) {
        if (isNotturno.value && !request.date) return;
        const response = isNotturno.value
          ? await bookingAPI.getRangeRestaurant(request)
          : await bookingAPI.getRangePickup(request);

        pickup_return.simba_minute = response.data.minute;

        pickup_return.simba_km = response.data.km;
        pickup_return.simba_imponibile = response.data.imponibile;
        pickup_return.simba_km_range = response.data.kmRange;

        responseKmReturn.value = response.data.kmRange;
        responseCostReturn.value = response.data.cost;

        store.destinationPointAddress = request.destination_point.address;
        store.destinationPointStreetNumber =
          request.destination_point.street_number;
        store.destinationPointCity = request.destination_point.city;
        store.destinationPointCap = request.destination_point.cap;
        store.destinationPointProvince = request.destination_point.province;
      }
    };

    calcolaKmReturn();

    isAvantiButtonDisabled();

    // ---- lavori (passo 2) ----
    const erroreLavoro = ref(null); // { riga, campo, messaggio }
    const aggiungiLavoro = () => {
      booking.tasks.push({ description: '', cost: 0, pay: false, accepted: false });
    };
    const rimuoviLavoro = (i) => {
      booking.tasks.splice(i, 1);
      erroreLavoro.value = null;
    };
    const totaleLavori = computed(() => booking.tasks.reduce((t, x) => t + importo(x.cost), 0));
    const totaleLavoriApp = computed(() => booking.tasks.filter((x) => x.pay).reduce((t, x) => t + importo(x.cost), 0));

    // ---- riepilogo (passo 2) ----
    const nomeCliente = computed(() => {
      const c = booking.customer || {};
      return [c.name, c.surname].filter(Boolean).join(' ') || c.company_name || c.mail || '';
    });
    const nomeAuto = computed(() => {
      const a = booking.car || {};
      return [[a.marca, a.modello].filter(Boolean).join(' '), a.targa].filter(Boolean).join(' · ');
    });
    const quando = (d) => {
      if (!d) return '—';
      const g = dayjs(d);
      if (!g.isValid()) return '—';
      const giorno = g.toDate().toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
      return `${giorno} · ${g.format('HH:mm')}`;
    };
    const creazione = ref(false);

    const addBookingNew = async (booking) => {
      let response = {};
      let headquarter ={
        address:booking.headquarter.address,
        street_number:booking.headquarter.street_number,
        cap:booking.headquarter.cap,
        city:booking.headquarter.city,
        province:booking.headquarter.province,
        latitude:booking.headquarter.latitude,
        longitude:booking.headquarter.longitude,
}

      try {
        // Dopo un tentativo fallito si resta sul form: senza questo reset il
        // secondo invio ripartirebbe con le tratte e i planner gia' accodati
        // dal primo, creando un booking con gambe duplicate.
        booking.pickup_bookings.splice(0);
        pickup_gone.planners.splice(0);
        pickup_return.planners.splice(0);

        booking.loaner =
          typeof booking.loaner === 'string'
            ? booking.loaner.toLowerCase() === 'true'
            : booking.loaner === true;
        if (booking.loaner) {
          booking.type_prenotazione = 'pickup_loaner';
        }

        let newPlannerGone = null;
        let newPlannerReturn = null;

        if (is_gone.value && is_return.value) {
          booking.type = 'both';
          booking.data = pickup_gone.date_partner;
          booking.imponibile =
            pickup_gone.simba_imponibile + pickup_return.simba_imponibile;
          booking.tot_fattura = booking.imponibile * 1.22;

          if (booking.loaner) {
            newPlannerGone = await addPlannerNew(
              headquarter,
              toRaw(pickup_gone.point),
              pickup_gone.date_partner,
              'PICKUP_AUTO_GONE'
            );
            pickup_gone.planners.push(newPlannerGone.data.plannerDriver);

            newPlannerReturn = await addPlannerNew(
              headquarter,
              toRaw(pickup_return.point),
              pickup_return.date_partner,
              'PICKUP_AUTO_RETURN'
            );
            pickup_return.planners.push(newPlannerReturn.data.plannerDriver);

            pickup_gone.date_customer =
              newPlannerGone.data.driver.trips[0].type == 'date_c1_p1'
                ? newPlannerGone.data.driver.trips[0].dateStart
                : newPlannerGone.data.driver.trips[1].dateStart;
            pickup_gone.date_driver =
              newPlannerGone.data.driver.trips[0].type == 'date_p1_c1'
                ? newPlannerGone.data.driver.trips[0].dateStart
                : newPlannerGone.data.driver.trips[1].dateStart;

            pickup_return.date_customer =
              newPlannerReturn.data.driver.trips[0].type == 'date_c1_p1'
                ? newPlannerReturn.data.driver.trips[0].dateStart
                : newPlannerReturn.data.driver.trips[1].dateStart;
            pickup_return.date_driver =
              newPlannerReturn.data.driver.trips[0].type == 'date_p1_c1'
                ? newPlannerReturn.data.driver.trips[0].dateStart
                : newPlannerReturn.data.driver.trips[1].dateStart;

          } else {
            newPlannerGone = await addPlannerNew(
              headquarter,
              toRaw(pickup_gone.point),
              pickup_gone.date_partner,
              'PICKUP_GONE'
            );
            pickup_gone.planners.push(newPlannerGone.data.plannerDriver);
            pickup_gone.planners.push(newPlannerGone.data.plannerJolly);


            newPlannerReturn = await addPlannerNew(
              headquarter,
              toRaw(pickup_return.point),
              pickup_return.date_partner,
              'PICKUP_RETURN'
            );

            pickup_return.planners.push(newPlannerReturn.data.plannerDriver);
            pickup_return.planners.push(newPlannerReturn.data.plannerJolly);

            pickup_gone.date_customer =
              newPlannerGone.data.driver.trips[0].type == 'date_c1_p1'
                ? newPlannerGone.data.driver.trips[0].dateStart
                : newPlannerGone.data.driver.trips[1].dateStart;
            pickup_gone.date_driver =
              newPlannerGone.data.driver.trips[0].type == 'date_p1_c1'
                ? newPlannerGone.data.driver.trips[0].dateStart
                : newPlannerGone.data.driver.trips[1].dateStart;

            pickup_return.date_customer =
              newPlannerReturn.data.driver.trips[0].type == 'date_c1_p1'
                ? newPlannerReturn.data.driver.trips[0].dateStart
                : newPlannerReturn.data.driver.trips[1].dateStart;
            pickup_return.date_driver =
              newPlannerReturn.data.driver.trips[0].type == 'date_p1_c1'
                ? newPlannerReturn.data.driver.trips[0].dateStart
                : newPlannerReturn.data.driver.trips[1].dateStart;

          }

          booking.pickup_bookings.push(pickup_gone);
          booking.pickup_bookings.push(pickup_return);
        } else if (is_gone.value && !is_return.value) {
          booking.type = 'gone';
          booking.data = pickup_gone.date_partner;
          booking.imponibile = pickup_gone.simba_imponibile;
          booking.tot_fattura = booking.imponibile * 1.22;

          if (booking.loaner) {
            newPlannerGone = await addPlannerNew(
              headquarter,
              toRaw(pickup_gone.point),
              pickup_gone.date_partner,
              'PICKUP_AUTO_GONE'
            );
            pickup_gone.planners.push(newPlannerGone.data.plannerDriver);

            pickup_gone.date_customer =
              newPlannerGone.data.driver.trips[0].type == 'date_c1_p1'
                ? newPlannerGone.data.driver.trips[0].dateStart
                : newPlannerGone.data.driver.trips[1].dateStart;
            pickup_gone.date_driver =
              newPlannerGone.data.driver.trips[0].type == 'date_p1_c1'
                ? newPlannerGone.data.driver.trips[0].dateStart
                : newPlannerGone.data.driver.trips[1].dateStart;

          } else {
            newPlannerGone = await addPlannerNew(
              headquarter,
              toRaw(pickup_gone.point),
              pickup_gone.date_partner,
              'PICKUP_GONE'
            );
            pickup_gone.planners.push(newPlannerGone.data.plannerDriver);
            pickup_gone.planners.push(newPlannerGone.data.plannerJolly);

            pickup_gone.date_customer =
              newPlannerGone.data.driver.trips[0].type == 'date_c1_p1'
                ? newPlannerGone.data.driver.trips[0].dateStart
                : newPlannerGone.data.driver.trips[1].dateStart;
            pickup_gone.date_driver =
              newPlannerGone.data.driver.trips[0].type == 'date_p1_c1'
                ? newPlannerGone.data.driver.trips[0].dateStart
                : newPlannerGone.data.driver.trips[1].dateStart;

          }

          booking.pickup_bookings.push(pickup_gone);
        } else if (!is_gone.value && is_return.value) {
          booking.type = 'return';
          booking.data = pickup_return.date_partner;
          booking.imponibile = pickup_return.simba_imponibile;
          booking.tot_fattura = booking.imponibile * 1.22;

          if (booking.loaner) {
            newPlannerReturn = await addPlannerNew(
              headquarter,
              toRaw(pickup_return.point),
              pickup_return.date_partner,
              'PICKUP_AUTO_RETURN'
            );
            pickup_return.planners.push(newPlannerReturn.data.plannerDriver);

            pickup_return.date_customer =
              newPlannerReturn.data.driver.trips[0].type == 'date_c1_p1'
                ? newPlannerReturn.data.driver.trips[0].dateStart
                : newPlannerReturn.data.driver.trips[1].dateStart;
            pickup_return.date_driver =
              newPlannerReturn.data.driver.trips[0].type == 'date_p1_c1'
                ? newPlannerReturn.data.driver.trips[0].dateStart
                : newPlannerReturn.data.driver.trips[1].dateStart;

          } else {
            newPlannerReturn = await addPlannerNew(
              headquarter,
              toRaw(pickup_return.point),
              pickup_return.date_partner,
              'PICKUP_RETURN'
            );
            pickup_return.planners.push(newPlannerReturn.data.plannerDriver);
            pickup_return.planners.push(newPlannerReturn.data.plannerJolly);

            pickup_return.date_customer =
              newPlannerReturn.data.driver.trips[0].type == 'date_c1_p1'
                ? newPlannerReturn.data.driver.trips[0].dateStart
                : newPlannerReturn.data.driver.trips[1].dateStart;
            pickup_return.date_driver =
              newPlannerReturn.data.driver.trips[0].type == 'date_p1_c1'
                ? newPlannerReturn.data.driver.trips[0].dateStart
                : newPlannerReturn.data.driver.trips[1].dateStart;

          }

          booking.pickup_bookings.push(pickup_return);
        }

        // Valet facoltativo: `use_valet: false` SOLO se l'operatore l'ha spento
        // su un pacchetto che il form ha mostrato. Acceso non si manda nulla:
        // assente è il default del backend. Si toglie ad ogni invio, così un
        // secondo tentativo dopo averlo riacceso non si porta dietro il false.
        delete booking.use_valet;
        if (valetInUso.value && !applicaValet.value) {
          booking.use_valet = false;
        }

        response = await prenotazioneAPI.addBooking(booking);

        // La notifica e' best-effort: il booking a questo punto E' gia' stato
        // creato, quindi un errore qui non deve far dire "creazione fallita"
        // (l'utente riproverebbe e ne creerebbe un secondo).
        try {
          await prenotazioneAPI.addBookingNotification({
            customer: booking.customer,
            booking: response.data,
            area: booking.headquarter.area,
          });
        } catch (notifyError) {
          console.error('Notifica nuovo booking fallita', notifyError);
        }

        return response;
      } catch (error) {
        notifyBookingError(error);
        console.error('addBookingNew', error);
        return null;
      }
    };

    /** Mostra l'errore solo se non l'ha gia' mostrato l'interceptor di API.js
     *  (che gestisce tutti gli status HTTP): evita il doppio toast. */
    const notifyBookingError = (error) => {
      if (error?.isAxiosError || error?.response) return;
      ElMessage({
        message:
          error?.message || 'Errore durante la creazione della prenotazione',
        type: 'error',
      });
    };

    const addPlannerNew = async (
      originPoint,
      destinationPoint,
      partnerTime,
      type
    ) => {
      const data = {
        originPoint: originPoint,
        destinationPoint: destinationPoint,
        partnerTime: partnerTime,
        type: type,
        area: booking.headquarter.area,
        // Con l'id della sede il backend cerca i driver per provincia.
        headquarterId: booking.headquarter.id,
        // Assegnazione MANUALE, come nel gestionale admin: il motore calcola
        // gli orari e verifica la copertura, ma i planner nascono senza driver.
        assign: false,
      };

      const res = await prenotazioneAPI.calcDuration(data);

      // ATTENZIONE: /planner/best-driver NON usa gli status HTTP. Quando non
      // trova un driver risponde 200 con { error: "No drivers available..." }.
      // Senza questo controllo quel payload proseguiva come se fosse un
      // planner valido e la creazione esplodeva piu' avanti su
      // `data.driver.trips`, dentro un catch muto.
      if (res?.data?.error || !res?.data?.driver) {
        throw new Error(
          res?.data?.error === 'No drivers available for this hour'
            ? "Nessun driver disponibile per l'orario scelto: scegli un altro slot."
            : res?.data?.error ||
              'Il planner non ha restituito un driver: prenotazione non creata.'
        );
      }

      return res;
    };

    let highlightDescription = ref(false);
    let highlightCost = ref(false);

    // I lavori sono FACOLTATIVI: una riga lasciata vuota (niente descrizione,
    // costo 0) vuol dire «nessun lavoro» e non si invia.
    const rigaLavoriVuota = (task) =>
      !String(task.description ?? '').trim() && !Number(task.cost || 0);

    // Il costo arriva da `el-input-number`: è già un numero (o null se il
    // campo è stato svuotato), niente più virgole da intercettare a mano.
    const checkTask = () => {
      erroreLavoro.value = null;
      for (let i = 0; i < booking.tasks.length; i += 1) {
        const task = booking.tasks[i];
        if (!String(task.description ?? '').trim()) {
          erroreLavoro.value = { riga: i, campo: 'descrizione', messaggio: `Lavoro ${i + 1}: scrivi la descrizione, oppure elimina la riga.` };
          return false;
        }
        if (task.cost === null || task.cost === undefined || !Number.isFinite(Number(task.cost)) || Number(task.cost) < 0) {
          erroreLavoro.value = { riga: i, campo: 'costo', messaggio: `Lavoro ${i + 1}: inserisci un costo valido.` };
          return false;
        }
      }
      return true;
    };

    const onSubmit = async () => {
      if (creazione.value) return;
      // Le righe vuote non si controllano e non si inviano: altrimenti il
      // booking nascerebbe con un task senza descrizione.
      booking.tasks = booking.tasks.filter((t) => !rigaLavoriVuota(t));
      if (!checkTask()) {
        return;
      }
      booking.tasks.forEach((t) => { t.cost = Number(t.cost); });

      creazione.value = true;
      try {
        const response = await addBookingNew(toRaw(booking));

        // In caso di fallimento addBookingNew restituisce null e ha già
        // mostrato il messaggio: si resta sul form, senza perdere i dati.
        if (response?.status !== 201) {
          return;
        }

        if (preventivi.value.length) {
          const fd = new FormData();
          preventivi.value.forEach((f) => fd.append('files', f, f.name));
          await fileLoad(fd);
        } else {
          await prenotazioneAPI.mailCustomer(booking.customer.mail);
        }

        ElMessage({
          message: 'Prenotazione creata',
          type: 'success',
        });

        router.push('/');
      } finally {
        creazione.value = false;
      }
    };

    const props1 = {
      expandTrigger: 'hover',
    };

    const isInputFocused = ref(false);

    const handleInputFocusGone = () => {
      isInputFocused.value = true;
    };
    const handleInputBlurGone = async () => {
      isInputFocused.value = false;

      if (
        pickup_gone.point.address == '' ||
        pickup_gone.point.street_number == '' ||
        pickup_gone.point.city == '' ||
        pickup_gone.point.cap == '' ||
        pickup_gone.point.province == ''
      ) {
        button1_disabled.value = true;
        selectedSlotGone.value = null;
      } else {
        loadSlotGone(pickup_gone.date_partner);

        let resposnePointGone = await loadPoints(pickup_gone.point);

        pickup_gone.point.label = resposnePointGone[0].label;
        pickup_gone.point.latitude = resposnePointGone[0].point.latitude;
        pickup_gone.point.longitude = resposnePointGone[0].point.longitude;

        store.originPointAddress = pickup_gone.point.address;
        store.originPointStreetNumber = pickup_gone.point.street_number;
        store.originPointCity = pickup_gone.point.cap;
        store.originPointCap = pickup_gone.point.city;
        store.originPointProvince = pickup_gone.point.province;
        button1_disabled.value = false;
        // }
        calcolaKmGone();
      }
    };

    const handleInputFocusReturn = () => {
      isInputFocused.value = true;
    };
    const handleInputBlurReturn = async () => {
      isInputFocused.value = false;
      if (
        pickup_return.point.address == '' ||
        pickup_return.point.street_number == '' ||
        pickup_return.point.city == '' ||
        pickup_return.point.cap == '' ||
        pickup_return.point.province == ''
      ) {
        button1_disabled.value = true;
        selectedSlotReturn.value = null;
      } else {
        loadSlotReturn(pickup_return.date_partner);

        let resposnePoinReturn = await loadPoints(pickup_return.point);

        pickup_return.point.label = resposnePoinReturn[0].label;
        pickup_return.point.latitude = resposnePoinReturn[0].point.latitude;
        pickup_return.point.longitude = resposnePoinReturn[0].point.longitude;

        store.destinationPointAddress = pickup_return.point.address;
        store.destinationPointStreetNumber = pickup_return.point.street_number;
        store.destinationPointCity = pickup_return.point.cap;
        store.destinationPointCap = pickup_return.point.city;
        store.destinationPointProvince = pickup_return.point.province;
        button1_disabled.value = false;
        calcolaKmReturn();
      }
    };

    const back = () => {
      active.value = 0;
    };

    const isInputFocusedDate = ref(false);

    const handleInputFocusDateGone = () => {
      isInputFocusedDate.value = true;
    };
    const handleInputBlurDateGone = () => {
      isInputFocusedDate.value = false;
    };

    const handleChangeCost = (value, task) => {
      task.value.cost = value;
    };

    const handleChangePay = (value, task) => {
      task.value.pay = value;
    };


    // Il partner inserisce da oggi in poi: i giorni passati li registra Simba.
    const disabledDate = (vDate) => dayjs(vDate).isBefore(dayjs(), 'day');

    const disabledDateReturn = (vDate) => {
      if (is_gone.value && pickup_gone.date_partner) {
        return dayjs(vDate).isBefore(dayjs(pickup_gone.date_partner), 'day');
      }
      return disabledDate(vDate);
    };

  

    // ---- arrivo dalla scheda cliente ------------------------
    // I clienti arrivano in modo asincrono (`preparaNuovaPrenotazione`): si
    // aspetta che quello scelto sia nell'elenco, poi si sceglie il cliente
    // (indirizzi e auto) e l'auto, come a mano. Una volta sola: se
    // l'operatore cambia cliente, non lo si rimette.
    let preselezioneFatta = false;
    const applicaPreselezione = async () => {
      if (preselezioneFatta || !props.clienteIniziale) return;
      if (!customers.some((c) => c.id === props.clienteIniziale)) return;
      preselezioneFatta = true;
      await handleChangeCustomer(props.clienteIniziale);
      if (props.autoIniziale) await handleChangeCar(props.autoIniziale);
    };
    watch(() => customersId.length, applicaPreselezione, { immediate: true });

    return {
      totaleFormattato,
      totaleSenzaValetFormattato,
      riepilogoTratte,
      euroTesto,
      euroTesto0,
      numeroIt,
      fasciaTesto,
      percorsoTesto,
      isNotturno,
      valetInUso,
      valetApplicato,
      valetAttivo,
      applicaValet,
      stimaValet,
      pacchettiUsabili,
      percentualeValet,
      dataBreve,
      erroreLavoro,
      aggiungiLavoro,
      rimuoviLavoro,
      totaleLavori,
      totaleLavoriApp,
      nomeCliente,
      nomeAuto,
      quando,
      preventivi,
      aggiungiPreventivi,
      dimensioneFile,
      creazione,
      loadingSlotsGone,
      loadingSlotsReturn,
      back,
      handleInputFocusGone,
      handleInputBlurGone,
      handleInputFocusReturn,
      handleInputBlurReturn,
      fileLoad,
      isAvantiButtonDisabled,
      headquarterSelected,
      customerSelected,
      carSelected,
      handleChangeDateGone,
      handleChangeDateReturn,
      pickup_gone,
      pickup_return,

      calcolaKmGone,
      calcolaKmReturn,
      responseKmGone,
      responseCostGone,
      responseKmReturn,
      responseCostReturn,

      changeLoaner,
      deleteGone,
      is_gone,
      deleteReturn,
      is_return,
      selectedSlotGone,
      selectedSlotReturn,
      timeSlotsGone,
      timeSlotsReturn,
      handleSlotChangeGone,
      handleSlotChangeReturn,

      show_minute,
      active,
      next,
      tasks,
      loadCars,
      addBookingNew,
      customers,
      customersId,
      booking,

      carsId,
      headquartersId,
      headquarters,
      onSubmit,
      props1,
      handleChangeCar,

      handleChangeCustomer,
      handleChangeHeadquarter,
      Delete,
      button1_disabled,
      button2_disabled,
      residual_minuteVal,

      handleInputFocusDateGone,
      handleInputBlurDateGone,

      formRulePickup,
      formRuleBooking,

      highlightCustomer,
      highlightHeadquarter,
      highlightCar,
      highlightDateGone,
      highlightDateReturn,
      highlightSlotGone,
      highlightSlotReturn,
      SlotGone,
      SlotReturn,

      highlightDescription,
      highlightCost,
      SelectComponent,
      DescriptionComponent,
      loadPoints,
      loadingPoint,

      handleChangeCost,
      handleChangePay,

      customerExist,
      checkCustomerExist,

      disabledDate,
      disabledDateReturn,
    };
  },
};
</script>

<!--
  ⚠️ `scoped` NON e' cosmetico. Senza, queste 17 classi finivano nel foglio
  globale e si scontravano con quelle omonime di Escort e Restaurant (`.box-card`
  qui e' 100%, li' e' 1250x1000px; `.form-label` qui e' un flex, li' e' un testo
  con margini negativi) e con `.form-section` di global.css, che e' tutt'altro.
  Vinceva l'ultimo foglio caricato. `.flex-container` e `.select-component` sono
  uscite di qui e stanno in `styles/forms.css` perche' le usano anche altre
  pagine: vedi la nota la' dentro.
-->
<style scoped>
/* Nessuna regola locale.

   Tutte le classi di layout di questo form (`.container`, `.container-data`,
   `.container-point`, `.container-customer`, `.info-container`, `.task-row`,
   `.custom-switch`, `.azioni-row`) sono in `styles/forms.css`: sono le STESSE
   di EscortComponent e RestaurantComponent, e tenerle scritte tre volte era
   il motivo per cui si scavalcavano a vicenda. Vedi la nota la' dentro.

   Qui sotto solo cio' che esiste SOLO in Pickup: fascia km con i km veri,
   riquadro del valet, secondo passo (lavori, note, preventivo, riepilogo). */

/* ---------- fascia km e tariffa della tratta ----------
   Riquadri invece di pastiglie: la fascia ha una seconda riga, i km e i
   minuti veri, che in una pastiglia non sta. */
.fx-stats .fx-stat {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 140px;
  padding: 8px 14px;
  border-radius: 6px;
  background: var(--color-bg-primary, #fff);
}
.fx-stats .fx-stat__v { font-size: 16px; }
.fx-stat__sub {
  font-size: 11.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted, #a3a3a3);
}
/* Con il valet la tariffa della tratta non si paga: resta leggibile, barrata. */
.fx-stat--barrata .fx-stat__v {
  color: var(--color-text-muted, #a3a3a3);
  text-decoration: line-through;
}
.pk-was {
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted, #a3a3a3);
}

/* ---------- pacchetto valet ---------- */
.vl { display: flex; flex-direction: column; gap: 6px; margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--color-bg-tertiary, #f5f5f5); }
.vl__empty { margin: 0; font-size: 12.5px; color: var(--color-text-secondary, #737373); }
.vl__card { border: 1px solid var(--color-border, #e5e5e5); border-radius: 6px; background: var(--color-bg-secondary, #fafafa); overflow: hidden; }
.vl__top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 14px; border-bottom: 1px solid var(--color-border, #e5e5e5); }
.vl__title { font-size: 14px; font-weight: 700; }
.vl__switch { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 600; color: var(--color-text-secondary, #737373); cursor: pointer; }
.vl__tag { font-size: 11px; font-weight: 700; color: var(--state-ok, #1e7a4d); background: var(--state-ok-bg, #dcf1e6); border: 1px solid var(--state-ok-line, #a9ddc1); border-radius: 4px; padding: 1px 8px; }
.vl__stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 20px; padding: 12px 14px; background: var(--color-bg-primary, #fff); }
.vl__stat { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.vl__k { font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary, #737373); }
.vl__v { font-size: 14px; font-weight: 600; font-variant-numeric: tabular-nums; }
.vl__v b { font-size: 18px; font-weight: 800; }
.vl__v small { margin-left: 3px; font-size: 12px; color: var(--color-text-muted, #a3a3a3); }
.vl__bar { display: block; height: 4px; margin-top: 2px; border-radius: 2px; background: var(--color-bg-tertiary, #f5f5f5); overflow: hidden; }
.vl__bar span { display: block; height: 100%; background: var(--state-ok, #1e7a4d); }
.vl__effect { padding: 10px 14px; border-top: 1px solid var(--color-border, #e5e5e5); font-size: 13px; line-height: 1.5; color: var(--color-text-secondary, #737373); }
.vl__effect b { color: var(--color-text-primary, #171717); }
.vl__more { margin: 0; padding: 0 14px 10px; font-size: 12px; color: var(--color-text-muted, #a3a3a3); }

/* ---------- passo 2 ---------- */
.lv { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 16px; align-items: start; }
.lv__main { display: flex; flex-direction: column; min-width: 0; }
.lv__aside { position: sticky; top: 12px; }
.lv__count { font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; color: var(--color-text-secondary, #737373); background: var(--color-bg-tertiary, #f5f5f5); border-radius: 4px; padding: 0 7px; }

.lv__grid { display: grid; grid-template-columns: minmax(0, 1fr) 150px 150px 30px; gap: 10px; align-items: center; }
.lv__grid--head { padding-bottom: 6px; border-bottom: 1px solid var(--color-border, #e5e5e5); }
.lv__grid--head span { font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary, #737373); }
.lv__row { padding: 8px 0; border-bottom: 1px solid var(--color-bg-tertiary, #f5f5f5); }
.lv__cost { width: 100%; }
.lv__pay { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--color-text-secondary, #737373); cursor: pointer; }
.lv__del { width: 28px; height: 28px; border: 1px solid var(--color-border, #e5e5e5); border-radius: 6px; background: #fff; color: var(--state-danger, #a82820); font-size: 16px; line-height: 1; cursor: pointer; }
.lv__del:hover { border-color: var(--state-danger, #a82820); }
.lv__del:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.is-err :deep(.el-input__wrapper) { box-shadow: 0 0 0 1px var(--el-color-danger) inset; }
.lv__empty { margin: 4px 0 8px; font-size: 13px; color: var(--color-text-secondary, #737373); }
.lv__foot { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-top: 12px; }
.lv__sum { font-size: 12.5px; color: var(--color-text-secondary, #737373); font-variant-numeric: tabular-nums; }
.lv__sum b { color: var(--color-text-primary, #171717); }
.lv__err { margin: 8px 0 0; font-size: 12.5px; font-weight: 600; color: var(--state-attn, #c2410c); }

.lv__drop { position: relative; display: flex; align-items: center; gap: 12px; padding: 14px 16px; border: 1px dashed var(--el-border-color-dark, #d4d7de); border-radius: 6px; background: var(--color-bg-secondary, #fafafa); font-size: 13px; cursor: pointer; }
.lv__drop:hover, .lv__drop:focus-within { border-color: var(--color-primary); }
.lv__drop b { color: var(--color-primary); }
.lv__drop small { display: block; font-size: 11.5px; color: var(--color-text-muted, #a3a3a3); }
.lv__dropIcon { font-size: 20px; color: var(--color-text-secondary, #737373); }
.lv__file { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
.lv__files { list-style: none; margin: 10px 0 0; padding: 0; }
.lv__fileRow { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-bottom: 1px solid var(--color-bg-tertiary, #f5f5f5); }
.lv__fileRow:last-child { border-bottom: 0; }
.lv__fileName { flex: 1 1 auto; min-width: 0; font-size: 13px; font-weight: 600; overflow-wrap: anywhere; }
.lv__fileSize { font-size: 12px; color: var(--color-text-muted, #a3a3a3); font-variant-numeric: tabular-nums; }

.lv__summary { gap: 0; }
.lv__kv { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding: 7px 0; border-bottom: 1px solid var(--color-bg-tertiary, #f5f5f5); }
.lv__kv:last-child { border-bottom: 0; }
.lv__k { flex: none; font-size: 12.5px; color: var(--color-text-secondary, #737373); }
.lv__v { font-size: 13px; font-weight: 600; text-align: right; overflow-wrap: anywhere; }
.lv__v--ok { color: var(--state-ok, #1e7a4d); }
.lv__kv--total { margin-top: 4px; padding: 10px 12px; background: var(--color-bg-tertiary, #f5f5f5); border: 1px solid var(--color-border, #e5e5e5); border-radius: 4px; }
.lv__kv--total .lv__v { display: flex; align-items: baseline; gap: 8px; }
.lv__kv--total b { font-size: 16px; font-weight: 800; font-variant-numeric: tabular-nums; }

@media (max-width: 1000px) {
  .lv { grid-template-columns: minmax(0, 1fr); }
  .lv__aside { position: static; }
}
@media (max-width: 700px) {
  .vl__stats { grid-template-columns: 1fr 1fr; }
  .lv__grid--head { display: none; }
  .lv__grid { grid-template-columns: minmax(0, 1fr) 30px; }
  .lv__row > :first-child { grid-column: 1 / -1; }
}
</style>
